/**
 * PATH: packages/email-hub/src/feature/components/useComposeRailSearch.ts
 * FILE_ID: EMAILHUB-COMPOSE-RAIL-SEARCH-001
 * PURPOSE: Drive the shared Search mail row for the compose inbox rail.
 * WHY: The inbox controller also writes the URL and the scope tabs. This
 *      model feeds the same row into useEmails without mounting that controller.
 */
import { useCallback, useMemo, useState } from 'react'
import { Mail } from 'lucide-react'

import { useApiAuth } from '../../_internal/components/ApiAuthGate'
import { useToast } from '../../_internal/components/Toast'
import { useDebouncedValue } from '../../_internal/hooks/useDebouncedValue'
import type { SidebarFilters } from './filter-sidebar/types'
import {
  useClassifyUnprocessed,
  useEmails,
  useEmailStats,
} from '../hooks/useEmails'
import { useOrgCustomCategories, usePersonalCategories } from '../hooks/useEmailLabelPrefs'
import {
  CATEGORY_BAR_ORDER,
  CATEGORY_CONFIG,
  IMPORTANT_CATEGORIES,
  getCategoryConfig,
} from '../lib/categoryConfig'
import { buildInboxFilterPills } from '../lib/inboxFilterPills'
import {
  parseEmailSearchQuery,
  suggestEmailSearchTokens,
} from '../lib/parseEmailSearchQuery'
import { DEFAULT_EMAIL_SORT, sortEmailsBy, type EmailSortMode } from '../lib/sortEmails'
import { isSmartSearchEnabled, setSmartSearchEnabled } from '../lib/smartSearchFlag'
import { useMutedCategories } from '../stores/mutedCategoriesStore'
import type { SearchCommandModel } from '../pages/inbox-page/SearchCommandRow'
import { buildComposeRailEmailQuery } from './composeRailEmailQuery'

export function useComposeRailSearch() {
  const { session } = useApiAuth()
  const { toast } = useToast()
  const personalCategories = usePersonalCategories()
  const orgCustomCategories = useOrgCustomCategories()
  const mutedCategories = useMutedCategories()
  const customCategories = useMemo(
    () => [...personalCategories, ...orgCustomCategories],
    [personalCategories, orgCustomCategories],
  )
  const [searchQuery, setSearchQuery] = useState('')
  const [searchFocused, setSearchFocused] = useState(false)
  const [searchHighlight, setSearchHighlight] = useState(0)
  const [smartSearchOn, setSmartSearchOn] = useState(() => isSmartSearchEnabled())
  const [sortMode, setSortMode] = useState<EmailSortMode>(DEFAULT_EMAIL_SORT)
  const [selectedCategories, setSelectedCategories] = useState<string[]>([])
  const [isReadFilter, setIsReadFilter] = useState<boolean | undefined>()
  const [isStarredFilter, setIsStarredFilter] = useState<boolean | undefined>()
  const [allocationFilter, setAllocationFilter] = useState<'allocated' | 'unallocated' | undefined>()
  const [jobIdsIn, setJobIdsIn] = useState<number[]>([])
  const [fromAddressContains, setFromAddressContains] = useState('')
  const [receivedAtGte, setReceivedAtGte] = useState<string | undefined>()
  const [receivedAtLte, setReceivedAtLte] = useState<string | undefined>()
  const [hasAttachments, setHasAttachments] = useState<boolean | undefined>()
  const [hasCalendarEvent, setHasCalendarEvent] = useState<boolean | undefined>()
  const [mailboxKeys, setMailboxKeys] = useState<string[]>([])
  const [showSidebar, setShowSidebar] = useState(false)
  const [shortcutsPanelOpen, setShortcutsPanelOpen] = useState(false)
  const [groupByMailbox, setGroupByMailbox] = useState(false)
  const [hidePromotions, setHidePromotions] = useState(true)
  const [hideCleared, setHideCleared] = useState(true)
  const debouncedSearchQuery = useDebouncedValue(searchQuery, 250)
  const parsed = useMemo(
    () => parseEmailSearchQuery(debouncedSearchQuery),
    [debouncedSearchQuery],
  )
  const listParams = useMemo(() => buildComposeRailEmailQuery({
    debouncedSearch: debouncedSearchQuery,
    smartSearchOn,
    selectedCategories,
    hidePromotions,
    mutedCategories,
    allocation: allocationFilter,
    hideCleared,
    isRead: isReadFilter,
    isStarred: isStarredFilter,
    jobIds: jobIdsIn,
    mailboxKeys,
    fromAddressContains,
    receivedAtGte,
    receivedAtLte,
    hasAttachments,
    hasCalendarEvent,
  }), [
    allocationFilter, debouncedSearchQuery, fromAddressContains, hasAttachments,
    hasCalendarEvent, hideCleared, hidePromotions, isReadFilter, isStarredFilter,
    jobIdsIn, mailboxKeys, mutedCategories, receivedAtGte, receivedAtLte,
    selectedCategories, smartSearchOn,
  ])
  const list = useEmails(listParams)
  const { stats, refetch: refetchStats } = useEmailStats()
  const { busy: classifyBusy, run: runClassify } = useClassifyUnprocessed()
  const emails = useMemo(
    () => sortEmailsBy(list.emails, sortMode).slice(0, 50),
    [list.emails, sortMode],
  )

  const toggleSmartSearch = useCallback(() => {
    setSmartSearchOn((value) => {
      setSmartSearchEnabled(!value)
      return !value
    })
  }, [])
  const toggleCategory = useCallback((key: string) => {
    setSelectedCategories((previous) =>
      previous.includes(key)
        ? previous.filter((category) => category !== key)
        : [...previous, key],
    )
  }, [])
  const applyImportantCategories = useCallback(() => {
    setSelectedCategories([...IMPORTANT_CATEGORIES])
  }, [])
  const patchFilters = useCallback((patch: Partial<SidebarFilters>) => {
    if ('category' in patch) setSelectedCategories(patch.category ? [patch.category] : [])
    if ('isRead' in patch) setIsReadFilter(patch.isRead)
    if ('isStarred' in patch) setIsStarredFilter(patch.isStarred)
    if ('jobIds' in patch) setJobIdsIn(patch.jobIds || [])
    if ('mailboxKeys' in patch) setMailboxKeys(patch.mailboxKeys || [])
    if ('fromAddressContains' in patch) setFromAddressContains(patch.fromAddressContains || '')
    if ('receivedAtGte' in patch) setReceivedAtGte(patch.receivedAtGte)
    if ('receivedAtLte' in patch) setReceivedAtLte(patch.receivedAtLte)
    if ('hasAttachments' in patch) setHasAttachments(patch.hasAttachments)
    if ('hasCalendarEvent' in patch) setHasCalendarEvent(patch.hasCalendarEvent)
  }, [])
  const clearAllFilters = useCallback(() => {
    setSelectedCategories([])
    setIsReadFilter(undefined)
    setIsStarredFilter(undefined)
    setAllocationFilter(undefined)
    setSearchQuery('')
    setJobIdsIn([])
    setMailboxKeys([])
    setFromAddressContains('')
    setReceivedAtGte(undefined)
    setReceivedAtLte(undefined)
    setHasAttachments(undefined)
    setHasCalendarEvent(undefined)
  }, [])
  const applyPreset = useCallback((raw: Record<string, unknown>) => {
    setSelectedCategories(
      Array.isArray(raw.categories)
        ? raw.categories.filter((category): category is string => typeof category === 'string')
        : typeof raw.category === 'string' && raw.category ? [raw.category] : [],
    )
    setIsReadFilter(typeof raw.isRead === 'boolean' ? raw.isRead : undefined)
    setIsStarredFilter(typeof raw.isStarred === 'boolean' ? raw.isStarred : undefined)
    setJobIdsIn(Array.isArray(raw.jobIds)
      ? raw.jobIds.filter((jobId): jobId is number => typeof jobId === 'number')
      : [])
    setMailboxKeys(Array.isArray(raw.mailboxKeys)
      ? raw.mailboxKeys.filter((key): key is string => typeof key === 'string')
      : [])
    setFromAddressContains(typeof raw.fromAddressContains === 'string' ? raw.fromAddressContains : '')
    setReceivedAtGte(typeof raw.receivedAtGte === 'string' ? raw.receivedAtGte : undefined)
    setReceivedAtLte(typeof raw.receivedAtLte === 'string' ? raw.receivedAtLte : undefined)
    setHasAttachments(typeof raw.hasAttachments === 'boolean' ? raw.hasAttachments : undefined)
    setHasCalendarEvent(typeof raw.hasCalendarEvent === 'boolean' ? raw.hasCalendarEvent : undefined)
  }, [])
  const handleClassifyUnprocessed = useCallback(async () => {
    const limit = Math.max(1, Math.min(stats?.unprocessed ?? 200, 1000))
    const result = await runClassify(limit, false)
    if (!result) {
      toast('Could not start AI classification - please try again.', 'error')
      return
    }
    toast(`Classifying ${result.queued} email${result.queued === 1 ? '' : 's'} in the background…`, 'info')
    list.refetch()
    refetchStats()
  }, [list, refetchStats, runClassify, stats?.unprocessed, toast])
  const handleRecategorize = useCallback(async () => {
    const result = await runClassify(1000, true, true)
    if (!result) {
      toast('Could not start re-classification - please try again.', 'error')
      return
    }
    toast(`Re-classifying ${result.queued} email${result.queued === 1 ? '' : 's'} with the new rules…`, 'info')
    list.refetch()
    refetchStats()
  }, [list, refetchStats, runClassify, toast])

  const sidebarCategories = useMemo(
    () => CATEGORY_BAR_ORDER.map((key) => {
      const config = CATEGORY_CONFIG[key]
      return config
        ? { key, label: config.label, icon: config.icon }
        : { key, label: key, icon: Mail }
    }),
    [],
  )
  const sidebarFilters: SidebarFilters = {
    category: selectedCategories.length === 1 ? selectedCategories[0] : undefined,
    isRead: isReadFilter,
    isStarred: isStarredFilter,
    jobIds: jobIdsIn,
    mailboxKeys,
    fromAddressContains,
    receivedAtGte,
    receivedAtLte,
    hasAttachments,
    hasCalendarEvent,
  }
  const filterPills = useMemo(() => buildInboxFilterPills({
    selectedCategories,
    categoryLabel: (key) => getCategoryConfig(key, customCategories).label,
    isReadFilter,
    isStarredFilter,
    allocationFilter,
    jobIdsIn,
    fromAddressContains,
    companyDomain: '',
    receivedAtGte,
    receivedAtLte,
    hasAttachments,
    hasCalendarEvent,
    mailboxKeysExplicit: mailboxKeys,
    hiddenMailboxCount: 0,
    mutedCategoryCount: mutedCategories.length,
    searchTokens: parsed.recognisedOperators,
  }), [
    allocationFilter, customCategories, fromAddressContains, hasAttachments,
    hasCalendarEvent, isReadFilter, isStarredFilter, jobIdsIn, mailboxKeys,
    mutedCategories.length, parsed.recognisedOperators, receivedAtGte, receivedAtLte,
    selectedCategories,
  ])

  const model: SearchCommandModel = {
    searchQuery,
    setSearchQuery,
    setSearchHighlight,
    searchHighlight,
    searchFocused,
    setSearchFocused,
    liveSearchSuggestions: suggestEmailSearchTokens(searchQuery),
    debouncedSearchQuery,
    kanbanSession: session,
    sortMode,
    setSortMode,
    canGroupByMailbox: false,
    groupByMailbox,
    setGroupByMailbox,
    showSidebar,
    setShowSidebar,
    sidebarCategories,
    sidebarFilters,
    patchFilters,
    clearAllFilters,
    applyPreset,
    setShortcutsPanelOpen,
    smartSearchOn,
    toggleSmartSearch,
    stats,
    selectedCategories,
    setSelectedCategories,
    mutedCategories,
    customCategories,
    toggleCategory,
    applyImportantCategories,
    allocationFilter,
    setAllocationFilter,
    hidePromotions,
    setHidePromotions,
    hideCleared,
    setHideCleared,
    classifyBusy,
    handleClassifyUnprocessed,
    handleRecategorize,
    filterPills,
  }

  return {
    model,
    emails,
    loadingInitial: list.loadingInitial,
    refreshing: list.refreshing,
    error: list.error,
    refetch: list.refetch,
    unread: stats?.unread,
    shortcutsOpen: shortcutsPanelOpen,
    closeShortcuts: () => setShortcutsPanelOpen(false),
  }
}
